/**
 * useQBitChain — 链上直连交互 React Hook
 * 
 * 提供直接与 QBit Chain 交互的 React Hook，
 * 不走后端 API，直接通过 qbit-chain-sdk 与 RPC 节点通信。
 * 
 * 适用场景：
 * - DApp 前端直连链查询
 * - 钱包插件链上余额/交易显示
 * - 链状态监控面板
 * 
 * @example
 * function ChainDashboard() {
 *   const { chainStatus, balance, loading } = useQBitChain({
 *     address: myAddress,
 *     network: 'mainnet',
 *   });
 *   return <div>Balance: {balance?.qbt} QBT</div>;
 * }
 */

import { useState, useCallback, useEffect, useRef } from 'react';

function extractTransactionHash(result) {
    if (!result) return null;
    if (typeof result === 'string') return result;
    return result.txHash || result.tx_hash || result.signature || result.hash || null;
}

/**
 * QBit Chain 直连交互 Hook
 * 
 * @param {Object} options
 * @param {string} options.address - 查询地址（可选，传入后自动查余额和 Token）
 * @param {string} options.network - 网络 ('mainnet' | 'devnet')
 * @param {string} options.endpoint - 自定义 RPC 端点
 * @param {boolean} options.autoRefresh - 是否自动刷新（默认 false）
 * @param {number} options.refreshInterval - 刷新间隔（默认 30000ms）
 */
export function useQBitChain(options = {}) {
    const {
        address,
        network = 'mainnet',
        endpoint,
        autoRefresh = false,
        refreshInterval = 30000,
    } = options;

    // 状态
    const [chainStatus, setChainStatus] = useState(null);
    const [balance, setBalance] = useState(null);
    const [tokenAccounts, setTokenAccounts] = useState([]);
    const [validators, setValidators] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [connected, setConnected] = useState(false);

    // SDK 引用（延迟加载）
    const sdkRef = useRef(null);
    const connRef = useRef(null);
    const connectionKeyRef = useRef(null);
    const subRef = useRef(null);

    /**
     * 延迟加载 chain SDK
     */
    const ensureSdk = useCallback(async () => {
        const nextConnectionKey = JSON.stringify({ network, endpoint: endpoint || '' });
        if (sdkRef.current && connRef.current && connectionKeyRef.current === nextConnectionKey) {
            return;
        }

        try {
            const sdk = sdkRef.current || await import('@quantabit/qbit-chain-sdk');
            sdkRef.current = sdk;
            if (subRef.current?.destroy) {
                subRef.current.destroy();
                subRef.current = null;
            }
            if (connRef.current?.destroy) {
                connRef.current.destroy();
            }
            setConnected(false);
            connRef.current = new sdk.QBitConnection({
                network,
                endpoint,
                onConnect: () => setConnected(true),
                onDisconnect: () => setConnected(false),
            });
            connectionKeyRef.current = nextConnectionKey;
        } catch (err) {
            throw new Error(`需要安装 @quantabit/qbit-chain-sdk: ${err.message}`);
        }
    }, [network, endpoint]);

    /**
     * 获取链状态
     */
    const fetchChainStatus = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await ensureSdk();
            const status = await sdkRef.current.getChainStatus(connRef.current);
            setChainStatus(status);
            return status;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, [ensureSdk]);

    /**
     * 获取余额
     */
    const fetchBalance = useCallback(async (addr) => {
        const targetAddr = addr || address;
        if (!targetAddr) return null;

        try {
            await ensureSdk();
            const result = await sdkRef.current.getBalance(targetAddr, connRef.current);
            setBalance(result);
            return result;
        } catch (err) {
            setError(err.message);
            return null;
        }
    }, [address, ensureSdk]);

    /**
     * 获取 Token 持仓
     */
    const fetchTokenAccounts = useCallback(async (addr) => {
        const targetAddr = addr || address;
        if (!targetAddr) return [];

        try {
            await ensureSdk();
            const result = await sdkRef.current.getTokenAccounts(targetAddr, connRef.current);
            setTokenAccounts(result);
            return result;
        } catch (err) {
            setError(err.message);
            return [];
        }
    }, [address, ensureSdk]);

    /**
     * 获取验证者列表
     */
    const fetchValidators = useCallback(async () => {
        try {
            await ensureSdk();
            const result = await sdkRef.current.getValidators(connRef.current);
            setValidators(result);
            return result;
        } catch (err) {
            setError(err.message);
            return null;
        }
    }, [ensureSdk]);

    /**
     * 获取交易历史
     */
    const fetchTransactionHistory = useCallback(async (addr, opts = {}) => {
        const targetAddr = addr || address;
        if (!targetAddr) return [];

        try {
            await ensureSdk();
            return await sdkRef.current.getTransactionHistory(targetAddr, opts, connRef.current);
        } catch (err) {
            setError(err.message);
            return [];
        }
    }, [address, ensureSdk]);

    /**
     * 网络验证
     */
    const validateNetwork = useCallback(async () => {
        try {
            await ensureSdk();
            return await sdkRef.current.validateNetwork(connRef.current);
        } catch (err) {
            setError(err.message);
            return { valid: false, error: err.message };
        }
    }, [ensureSdk]);

    /**
     * 请求空投（仅 devnet / localnet）
     * 成功后自动刷新余额。
     */
    const requestAirdrop = useCallback(async (amountQBT, addr) => {
        const targetAddr = addr || address;
        if (!targetAddr) {
            throw new Error('Missing address for airdrop');
        }
        try {
            await ensureSdk();
            const signature = await sdkRef.current.requestAirdrop(targetAddr, amountQBT, connRef.current);
            // 异步刷新余额（不阻塞返回）
            fetchBalance(targetAddr).catch(() => {});
            return signature;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [address, ensureSdk, fetchBalance]);

    /**
     * 实时订阅余额变动，自动更新 balance 状态。
     * @param {string} addr - 监听地址（默认 options.address）
     * @returns {Promise<Function>} 取消订阅函数
     */
    const subscribeBalance = useCallback(async (addr) => {
        const targetAddr = addr || address;
        if (!targetAddr) {
            throw new Error('Missing address for balance subscription');
        }
        try {
            await ensureSdk();
            if (!subRef.current) {
                subRef.current = new sdkRef.current.QBitSubscription(connRef.current);
            }
            const id = subRef.current.onAccountChange(targetAddr, (info) => {
                setBalance({ lamports: info.lamports, qbt: info.lamports / 1_000_000_000 });
            });
            return () => {
                if (subRef.current) subRef.current.unsubscribe(id);
            };
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [address, ensureSdk]);

    /**
     * 构建 Memo 交易（不签名、不发送）
     * 适用于 DID/凭证/NFT 等业务 SDK 需要把哈希锚定到 QBit 主网的场景。
     */
    const buildMemoTransaction = useCallback(async (memo, opts = {}) => {
        const payerAddress = opts.address || address;
        if (!payerAddress) {
            throw new Error('Missing payer address');
        }

        try {
            await ensureSdk();
            const txBuilder = new sdkRef.current.QBitTransaction(connRef.current);
            txBuilder.addMemo(memo);
            if (opts.priorityFee) {
                txBuilder.setPriorityFee(opts.priorityFee);
            }
            return await txBuilder.build(payerAddress);
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [address, ensureSdk]);

    /**
     * 使用浏览器钱包签名并发送 Memo 交易
     */
    const sendMemoWithWallet = useCallback(async (provider, memo, opts = {}) => {
        if (!provider) {
            throw new Error('Missing QBit wallet provider');
        }

        const payerAddress =
            opts.address ||
            address ||
            provider.publicKey?.toBase58?.() ||
            provider.publicKey?.toString?.();

        if (!payerAddress) {
            throw new Error('Missing wallet address');
        }

        try {
            await ensureSdk();
            const transaction = await buildMemoTransaction(memo, {
                ...opts,
                address: payerAddress,
            });

            if (typeof provider.signAndSendTransaction === 'function') {
                const result = await provider.signAndSendTransaction(transaction, {
                    skipPreflight: opts.skipPreflight || false,
                    preflightCommitment: opts.preflightCommitment || 'confirmed',
                });
                return {
                    txHash: extractTransactionHash(result),
                    memo,
                    raw: result,
                };
            }

            if (typeof provider.signTransaction !== 'function') {
                throw new Error('Current wallet does not support signTransaction/signAndSendTransaction');
            }

            const signedTransaction = await provider.signTransaction(transaction);
            const txHash = await connRef.current.sendTransaction(signedTransaction, {
                skipPreflight: opts.skipPreflight || false,
                preflightCommitment: opts.preflightCommitment || 'confirmed',
            });

            return {
                txHash,
                memo,
                raw: signedTransaction,
            };
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [address, buildMemoTransaction, ensureSdk]);

    /**
     * 刷新全部数据
     */
    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await Promise.all([
                fetchChainStatus(),
                address ? fetchBalance() : Promise.resolve(),
                address ? fetchTokenAccounts() : Promise.resolve(),
            ]);
        } finally {
            setLoading(false);
        }
    }, [fetchChainStatus, fetchBalance, fetchTokenAccounts, address]);

    // 初始化
    useEffect(() => {
        refresh();
    }, [refresh]);

    // 自动刷新
    useEffect(() => {
        if (!autoRefresh) return;

        const timer = setInterval(() => {
            refresh();
        }, refreshInterval);

        return () => clearInterval(timer);
    }, [autoRefresh, refreshInterval, refresh]);

    // 清理
    useEffect(() => {
        return () => {
            if (subRef.current?.destroy) {
                subRef.current.destroy();
                subRef.current = null;
            }
            if (connRef.current?.destroy) {
                connRef.current.destroy();
                connRef.current = null;
                connectionKeyRef.current = null;
            }
        };
    }, []);

    return {
        // 状态
        chainStatus,
        balance,
        tokenAccounts,
        validators,
        loading,
        error,
        connected,

        // 操作
        fetchChainStatus,
        fetchBalance,
        fetchTokenAccounts,
        fetchValidators,
        fetchTransactionHistory,
        validateNetwork,
        requestAirdrop,
        subscribeBalance,
        buildMemoTransaction,
        sendMemoWithWallet,
        refresh,

        // 工具
        get connection() { return connRef.current; },
        get sdk() { return sdkRef.current; },
    };
}
